import * as React from 'react';
import BaseFormItem, {BaseFormItemProps} from '../../common/form-items/BaseFormItem';
import {debounce} from 'lodash';
import Config from '../../config';
import {Partner} from '../../api/dtos/dtos';
import {AutoComplete, Icon, Input} from 'antd';
import {DataSourceItemType} from 'antd/lib/auto-complete';
import {SelectValue} from 'antd/lib/select';
import {showDeleteConfirm} from '../../utils/dialogs';
import './PartnerAutoCompleteFormItem.css';
import locales from '../../locales';

interface Props extends BaseFormItemProps {
  partners: Partner[];
  onSelect?: (partner: Partner) => void;
  onSearch: (value: string) => void;
  onDelete: (id: number) => void;
}

interface State {
  partnerSuggestions: Partner[];
  open: boolean;
}

class PartnerAutoCompleteFormItem extends React.Component<Props, State> {

  public state: State = {
    partnerSuggestions: [],
    open: false,
  };

  private readonly debouncedPartnerSearch: (value: string) => void;

  constructor(props: Props) {
    super(props);
    this.debouncedPartnerSearch = debounce(this.onPartnerSearch, Config.SEARCH_DEBOUNCE_TIMEOUT).bind(this);
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    const {partners} = this.props;
    if (prevProps.partners !== partners) {
      this.setState({
        partnerSuggestions: partners,
        open: partners.length > 0
      });
    }
  }

  public render() {
    const {disabled} = this.props;
    const {open} = this.state;
    return (
      <BaseFormItem
        {...this.props}
        input={
          <AutoComplete
            className="mild-disabled"
            optionLabelProp="value"
            open={open}
            disabled={disabled}
            onBlur={this.hideSuggestions}
            onSearch={this.debouncedPartnerSearch}
            onSelect={this.onPartnerSelect}
            dataSource={this.renderPartnerOptions()}
          >
            <Input onClick={this.showSuggestions} suffix={<Icon type="search"/>}/>
          </AutoComplete>
        }
      />
    );
  }

  private renderPartnerOptions = (): DataSourceItemType[] => {
    return this.state.partnerSuggestions.map(p => (
      <AutoComplete.Option key={p.name} value={p.name}>
        <div className="partner-suggestion">
          <div>
            {p.name}<br/>
            {this.renderBankDetails(p)}
          </div>
          <Icon
            className="partner-delete-icon"
            type="delete"
            title={locales.delete}
            onClick={this.onPartnerDelete(p)}
          />
        </div>
      </AutoComplete.Option>
    ))
  };

  private onPartnerDelete = (partner: Partner) => (e: React.MouseEvent) => {
    e.stopPropagation();
    this.setState({open: false}, () => {
      showDeleteConfirm(() => this.props.onDelete(partner.id));
    });
  };

  private showSuggestions = () => {
    const {partnerSuggestions} = this.state;
    this.setState({open: partnerSuggestions.length > 0});
  };

  private hideSuggestions = () => {
    this.setState({open: false});
  };

  private renderBankDetails = (partner: Partner): React.ReactNode => {
    return (
      <>
        {partner.bankAccountNumber && this.renderDetailLabel(partner.bankAccountNumber)}
        {partner.bankName && <>{this.renderDetailLabel(` - ${partner.bankName}`)}</>}
        {partner.iban && <><br/>{this.renderDetailLabel(`IBAN: ${partner.iban}`)}</>}
      </>
    );
  };

  private renderDetailLabel = (detail: string): React.ReactNode => (
    <span style={{fontSize: 12, color: 'gray'}}>{detail}</span>
  );

  private onPartnerSelect = (value: SelectValue) => {
    const selectedPartner = this.state.partnerSuggestions.find(p => p.name === value);
    this.hideSuggestions();
    if (selectedPartner) {
      this.props.onSelect?.(selectedPartner);
    }
  };

  private onPartnerSearch = (value: string) => {
    if (value.length < 3) {
      this.setState({partnerSuggestions: [], open: false});
    } else {
      this.props.onSearch(value);
    }
  };
}

export default PartnerAutoCompleteFormItem;


// WEBPACK FOOTER //
// ./src/incoming-invoices/details/PartnerAutoCompleteFormItem.tsx